import { WorkspacesComponent } from '@umd-dit/workspaces-component-react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useDebugSettings } from "@/hooks/admin/useDebugSettings";
import { useToast } from "@/hooks/use-toast";

export default function OrganizationManagementPage() {
  const { selectedOrganizationId, selectOrganization, selectWorkspace, isAdmin, user } = useAuth();
  const { toast } = useToast();
  
  // Debug settings hook
  const { 
    data: debugSettings, 
    isLoading: isLoadingDebug,
    updateDebugSettings,
    isPending: isUpdatingDebug,
    mutationError: debugMutationError
  } = useDebugSettings();

  const handleDebugToggle = (checked: boolean) => {
    console.log('[OrganizationManagementPage] Debug toggle clicked:', checked);
    console.log('[OrganizationManagementPage] Current debug settings:', debugSettings);
    
    updateDebugSettings(checked);
    toast({
      title: checked ? 'Debug Mode Enabled' : 'Debug Mode Disabled',
      description: checked ? 'Detailed backend logging has been enabled for 2 hours.' : 'Detailed backend logging has been disabled.'
    });
  };
  
  // Debug logging for admin status
  console.log('[OrganizationManagementPage] isAdmin:', isAdmin);
  console.log('[OrganizationManagementPage] user:', user);
  console.log('[OrganizationManagementPage] Workspace API URL:', import.meta.env.VITE_WORKSPACES_API_URL);
  console.log('[OrganizationManagementPage] Debug settings:', debugSettings);
  console.log('[OrganizationManagementPage] Debug mutation error:', debugMutationError);
  
  return (
    <div className="container mx-auto p-6">
      {/* Admin Settings Section - For All Admins */}
      {isAdmin && (
        <div className="mb-8">
          <div className="mb-6">
            <h2 className="text-xl font-bold">Admin Settings</h2>
            <p className="text-muted-foreground">
              Manage system-wide settings for the legislative review application.
            </p>
          </div>
          
          <div className="space-y-6">
            {/* Debug Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Debug Settings</CardTitle>
                <CardDescription>
                  Enable detailed logging for troubleshooting backend issues
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <Label htmlFor="debug-mode" className="text-base font-normal">
                    Enable Debug Mode
                  </Label>
                  <Switch
                    id="debug-mode"
                    checked={debugSettings?.enabled || false}
                    onCheckedChange={handleDebugToggle}
                    disabled={isLoadingDebug || isUpdatingDebug}
                  />
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  {isLoadingDebug ? 'Loading debug settings...' :
                   isUpdatingDebug ? 'Updating debug settings...' :
                   debugSettings?.enabled 
                     ? `Debug mode is enabled. Last updated by ${debugSettings.updated_by || 'system'} at ${debugSettings.updated_at ? new Date(debugSettings.updated_at).toLocaleString() : 'unknown time'}`
                     : 'Debug mode is currently disabled'}
                </p>
                {debugMutationError && (
                  <p className="text-sm text-red-600 mt-2">
                    Error updating debug settings: {debugMutationError.message}
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Organization and Workspace Management Section */}
      <div>
        <div className="mb-6">
          <h2 className="text-xl font-bold">Organization & Workspace Management</h2>
          <p className="text-muted-foreground">
            Manage organizations and workspaces for the legislative review application.
          </p>
        </div>
        
        <WorkspacesComponent
          app_id="legislative-review"
          viewType={isAdmin ? "global-admin" : "user"}
          workspacesApiUrl={import.meta.env.VITE_WORKSPACES_API_URL}
          selectedOrganizationId={selectedOrganizationId || undefined}
          debug={import.meta.env.VITE_WORKSPACES_DEBUG_LOGGING === 'true'}
          onOrganizationSelect={selectOrganization}
          onWorkspaceSelect={selectWorkspace}
        />
      </div>
    </div>
  );
}